/**
 * Unit of Measurement (UoM) Constants
 *
 * These match the Prisma enums in apps/api/prisma/schema.prisma
 * and should be used across all frontend apps for consistency.
 */

/**
 * Base unit types for products
 * Represents the smallest sellable/countable unit of a product
 */
export const BASE_UNIT_TYPES = {
  TABLET: "TABLET",
  CAPSULE: "CAPSULE",
  ML: "ML",
  GM: "GM",
  MG: "MG",
  UNIT: "UNIT",
  PIECE: "PIECE",
  SACHET: "SACHET",
  AMPOULE: "AMPOULE",
  VIAL: "VIAL",
  TUBE: "TUBE",
  BOTTLE: "BOTTLE",
  INJECTION: "INJECTION",
  DROP: "DROP",
  SPRAY: "SPRAY",
  PATCH: "PATCH",
  SUPPOSITORY: "SUPPOSITORY",
  CREAM: "CREAM",
  OINTMENT: "OINTMENT",
  GEL: "GEL",
  LOTION: "LOTION",
  POWDER: "POWDER",
  SYRUP: "SYRUP",
  SUSPENSION: "SUSPENSION",
  SOLUTION: "SOLUTION",
  INHALER: "INHALER",
  DEVICE: "DEVICE",
  KIT: "KIT",
  SET: "SET",
  OTHER: "OTHER",
} as const;

export type BaseUnitType = keyof typeof BASE_UNIT_TYPES;

/**
 * Human-readable labels for base unit types
 */
export const BASE_UNIT_LABELS: Record<BaseUnitType, string> = {
  TABLET: "Tablet",
  CAPSULE: "Capsule",
  ML: "Milliliter (ml)",
  GM: "Gram (gm)",
  MG: "Milligram (mg)",
  UNIT: "Unit",
  PIECE: "Piece",
  SACHET: "Sachet",
  AMPOULE: "Ampoule",
  VIAL: "Vial",
  TUBE: "Tube",
  BOTTLE: "Bottle",
  INJECTION: "Injection",
  DROP: "Drop",
  SPRAY: "Spray",
  PATCH: "Patch",
  SUPPOSITORY: "Suppository",
  CREAM: "Cream",
  OINTMENT: "Ointment",
  GEL: "Gel",
  LOTION: "Lotion",
  POWDER: "Powder",
  SYRUP: "Syrup",
  SUSPENSION: "Suspension",
  SOLUTION: "Solution",
  INHALER: "Inhaler",
  DEVICE: "Device",
  KIT: "Kit",
  SET: "Set",
  OTHER: "Other",
};

/**
 * Grouped base unit types for better UX in dropdowns
 */
export const BASE_UNIT_GROUPS = {
  "Solid Dosage": ["TABLET", "CAPSULE", "POWDER", "SACHET"],
  Liquids: ["ML", "SYRUP", "SUSPENSION", "SOLUTION", "DROP"],
  "Semi-Solids": ["CREAM", "OINTMENT", "GEL", "LOTION"],
  Injectables: ["AMPOULE", "VIAL", "INJECTION"],
  "Topical/External": ["TUBE", "SPRAY", "PATCH", "SUPPOSITORY", "INHALER"],
  "Weight-Based": ["GM", "MG"],
  Packaging: ["BOTTLE", "PIECE", "UNIT"],
  Other: ["DEVICE", "KIT", "SET", "OTHER"],
} as const;

/**
 * Pack levels for product hierarchy
 * Defines how products are packaged and sold
 */
export const PACK_LEVELS = {
  BASE: "BASE", // Level 1: Single unit (1 tablet)
  PRIMARY: "PRIMARY", // Level 2: Primary pack (strip of 10 tablets)
  SECONDARY: "SECONDARY", // Level 3: Secondary pack (box of 10 strips)
  TERTIARY: "TERTIARY", // Level 4: Tertiary pack (case of 12 boxes)
  QUATERNARY: "QUATERNARY", // Level 5: Quaternary pack (pallet)
} as const;

export type PackLevel = keyof typeof PACK_LEVELS;

/**
 * Human-readable labels for pack levels
 */
export const PACK_LEVEL_LABELS: Record<PackLevel, string> = {
  BASE: "Base Unit",
  PRIMARY: "Primary Pack (e.g., Strip)",
  SECONDARY: "Secondary Pack (e.g., Box)",
  TERTIARY: "Tertiary Pack (e.g., Case)",
  QUATERNARY: "Quaternary Pack (e.g., Pallet)",
};

/**
 * Common pack names by base unit type
 * Helps suggest appropriate pack names
 */
export const COMMON_PACK_NAMES: Record<BaseUnitType, string[]> = {
  TABLET: ["Strip", "Box", "Blister", "Bottle"],
  CAPSULE: ["Strip", "Box", "Blister", "Bottle"],
  ML: ["Bottle", "Vial", "Ampoule", "Pack"],
  GM: ["Jar", "Tube", "Sachet", "Pack"],
  MG: ["Sachet", "Capsule", "Tablet", "Pack"],
  UNIT: ["Pack", "Box", "Set"],
  PIECE: ["Pack", "Box", "Set"],
  SACHET: ["Box", "Pack", "Carton"],
  AMPOULE: ["Tray", "Box", "Pack"],
  VIAL: ["Box", "Tray", "Pack"],
  TUBE: ["Box", "Pack", "Carton"],
  BOTTLE: ["Pack", "Box", "Carton"],
  INJECTION: ["Tray", "Box", "Pack"],
  DROP: ["Box", "Pack"],
  SPRAY: ["Box", "Pack"],
  PATCH: ["Box", "Pack", "Carton"],
  SUPPOSITORY: ["Strip", "Box", "Pack"],
  CREAM: ["Box", "Pack"],
  OINTMENT: ["Box", "Pack"],
  GEL: ["Box", "Pack"],
  LOTION: ["Box", "Pack"],
  POWDER: ["Sachet", "Jar", "Box"],
  SYRUP: ["Box", "Pack", "Carton"],
  SUSPENSION: ["Box", "Pack"],
  SOLUTION: ["Box", "Pack"],
  INHALER: ["Box", "Pack"],
  DEVICE: ["Box", "Pack", "Kit"],
  KIT: ["Box", "Pack"],
  SET: ["Box", "Pack"],
  OTHER: ["Pack", "Box", "Unit"],
};

/**
 * Get all base unit options as array for dropdowns
 */
export function getBaseUnitOptions(): Array<{
  value: BaseUnitType;
  label: string;
}> {
  return Object.entries(BASE_UNIT_LABELS).map(([value, label]) => ({
    value: value as BaseUnitType,
    label,
  }));
}

/**
 * Get grouped base unit options for select with optgroups
 */
export function getGroupedBaseUnitOptions(): Array<{
  group: string;
  options: Array<{ value: BaseUnitType; label: string }>;
}> {
  return Object.entries(BASE_UNIT_GROUPS).map(([group, units]) => ({
    group,
    options: units.map((unit) => ({
      value: unit as BaseUnitType,
      label: BASE_UNIT_LABELS[unit as BaseUnitType],
    })),
  }));
}

/**
 * Get pack level options as array for dropdowns
 */
export function getPackLevelOptions(): Array<{
  value: PackLevel;
  label: string;
}> {
  return Object.entries(PACK_LEVEL_LABELS).map(([value, label]) => ({
    value: value as PackLevel,
    label,
  }));
}
